import React, { type SyntheticEvent, useState } from "react";
import IconArrowLeft from "../../../../../../../common-deprecated/svg/toyota/arrow-left.svg";
import IconArrowRight from "../../../../../../../common-deprecated/svg/toyota/arrow-right.svg";
import { useBnbSelector } from "../../../../redux/store";
import { NavStep } from "../../../../../shared-logic/utils/Navigation";
import * as StepStyles from "./styles/BuildStepNavigation";
import * as Styles from "./styles/Navigation";
import { type BuildAndBuyLabelType, BnbLabel, useBnbLabel } from "../../../../utils/labels";
import { getAemGradesNavHeight } from "../../../../utils/typedUtils";
import {
    isMasterOrSlave,
    isCod,
    isDealer,
    isRTL,
} from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { isPassiveSummary } from "../../../../utils/settings/bnbSettingUtils";
import { trackNavBackStep, trackNavBackOverview, trackNavMobileOpen } from "../../../../utils/tracking";
import { SrOnly } from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import { isMergedEngineStep } from "../../../../utils/modelEngineUtils";
import useBackButton from "../../../../hooks/shared/useBackButton";

const stepLabels: Partial<Record<NavStep, BuildAndBuyLabelType>> = {
    [NavStep.Model]: "stepModelAndEngine",
    [NavStep.Engine]: "stepModelAndEngine",
    [NavStep.Customize]: "stepCustomise",
    [NavStep.Services]: "stepServices",
    [NavStep.Summary]: "stepSummaryAndSave",
    [NavStep.Buy]: "checkoutStep",
    [NavStep.Reserve]: "checkoutStep",
};

type PropsType = {
    children?: React.ReactChild;
    stepNumbers: Partial<Record<NavStep, number>>;
    stepTotal: number;
};

const BuildNavigation = (props: PropsType): JSX.Element | null => {
    const { children, stepNumbers, stepTotal } = props;
    const step = useBnbSelector((state) => state.navigation.step);
    const mobileBasketIsShown = useBnbSelector((state) => state.utils.showMobileBasket);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const isMergedModelStep = useBnbSelector((state) => isMergedEngineStep(state.bnbSettings, step));

    const trackingStep = isMergedModelStep ? NavStep.ModelEngine : step;

    const [navigationOpen, setNavigationOpen] = useState<boolean>(false);
    const toggleNavOpen = (event: SyntheticEvent): void => {
        event.preventDefault();
        if (!navigationOpen) trackNavMobileOpen(trackingStep);
        setNavigationOpen(!navigationOpen);
    };

    const fixedHeader = !isMasterOrSlave(commonSettings) && !isCod(commonSettings) && !isDealer(commonSettings);
    const gradeNavHeight = getAemGradesNavHeight();
    const backButton = useBackButton();

    const [ariaBuildNavigationMenu, ariaBuildNavigationBackButtonLabel] = useBnbLabel([
        "bnbA11yNavBuild",
        "bnbA11yNavButtonBack",
    ]);

    if (isPassiveSummary(commonSettings)) return null;
    return (
        <>
            {fixedHeader && (
                <Styles.DesktopStickyPadding
                    gradeNavHeight={gradeNavHeight} // Zaidan already adds padding for the main nav, but not the grades nav
                />
            )}
            <Styles.StickyWrapper
                id="or-build-navigation" // This ID is used in the getSiteNavigationOffset utility
                position={fixedHeader ? "fixed" : "relative"}
                top={fixedHeader ? gradeNavHeight : 0}
                zIndexName={mobileBasketIsShown ? "pageoverlayerExpansion" : "navBar"}
            >
                <Styles.DesktopNav id="or-build-navigation-wrapper" aria-labelledby="buildnavigation">
                    <SrOnly as="p" id="buildnavigation">
                        {ariaBuildNavigationMenu}
                    </SrOnly>
                    <SrOnly as="span">
                        <BnbLabel label="bnbA11yNavBuildDescription" />
                    </SrOnly>
                    {!!backButton && !commonSettings.query.codcustomer && (
                        <Styles.BackButton
                            as="a"
                            href={backButton.url}
                            onClick={() => {
                                if (backButton?.step) trackNavBackStep(trackingStep);
                                else trackNavBackOverview(trackingStep);
                            }}
                            title={ariaBuildNavigationBackButtonLabel}
                        >
                            <IconWrapper aria-hidden="true">
                                {isRTL(commonSettings) ? <IconArrowRight /> : <IconArrowLeft />}
                            </IconWrapper>
                        </Styles.BackButton>
                    )}
                    <Styles.NavWrapper>{children}</Styles.NavWrapper>
                </Styles.DesktopNav>

                <Styles.MobileNav id="or-build-navigation-wrapper-mobile">
                    <Styles.MobileNavHeader className="build-mobile-nav">
                        {!!backButton && !commonSettings.query.codcustomer && (
                            <Styles.BackButton
                                as="a"
                                href={backButton.url}
                                onClick={() => {
                                    if (backButton?.step) trackNavBackStep(trackingStep);
                                    else trackNavBackOverview(trackingStep);
                                }}
                                title={ariaBuildNavigationBackButtonLabel}
                            >
                                <IconWrapper aria-hidden="true">
                                    {isRTL(commonSettings) ? <IconArrowRight /> : <IconArrowLeft />}
                                </IconWrapper>
                            </Styles.BackButton>
                        )}
                        <Styles.MobileNavButton
                            type="button"
                            onClick={toggleNavOpen}
                            hasBasketButton={[NavStep.Customize, NavStep.Services, NavStep.Summary].includes(step)}
                            role="combobox"
                            aria-expanded={navigationOpen}
                            aria-owns="mobile-build-navigation"
                            title={ariaBuildNavigationMenu}
                        >
                            <Styles.StepIndicator>
                                <BnbLabel
                                    label="navigationStepOf"
                                    params={{
                                        current: stepNumbers[step],
                                        max: stepTotal,
                                    }}
                                />
                            </Styles.StepIndicator>
                            <StepStyles.StepText>
                                <BnbLabel label={stepLabels[step]!} />
                            </StepStyles.StepText>
                        </Styles.MobileNavButton>
                        <Styles.MobileNavList id="mobile-build-navigation">{children}</Styles.MobileNavList>
                    </Styles.MobileNavHeader>
                </Styles.MobileNav>
            </Styles.StickyWrapper>
        </>
    );
};

export default BuildNavigation;
